/**
 * PATH: packages/ui/src/kanban/components/KanbanCardPeekMeta.tsx
 * FILE_ID: UI-KANBAN-CARD-PEEK-META-001
 * PURPOSE: The at-a-glance identity strip at the top of the AI thread peek —
 *   WHO owns the card (assignee avatars), its priority, due date and tags.
 * WHY:
 *   FEAT-2026-06-21-KANBAN-CARD-PEEK-RICHEN — operator: "if you are going to
 *   expand down then you might as well have options also … the person face icon
 *   to see whose card it was … there is so much empty space … tags or something
 *   … what do you want to see here?". The peek used to jump straight from the
 *   title into the AI summary, wasting the top band. This pure row fills it with
 *   the context an operator scans first: members, priority, due, labels.
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────────────┐   ┌──────────────────┐
 *   │ card +       │ → │ derive avatars / priority / │ → │ one compact wrap │
 *   │ resolveMember│   │ overdue / tag chips         │   │ meta row         │
 *   └──────────────┘   └────────────────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - ./KanbanAvatar KanbanAvatarStack: the shared assignee avatar stack.
 *   - ../utils readableForeground: luminance-derived tag chip foreground.
 *   - lucide-react: unassigned / priority / due glyphs.
 * RELATED:
 *   - ./KanbanCardPeek.tsx: renders this directly under the header.
 *   - ./KanbanCardTags.tsx: the card-face tag row this mirrors.
 */
import { CalendarClock, Flag, UserRound } from 'lucide-react'

import type { KanbanMember } from '../api'
import type { Card as KanbanCardModel } from '../types'
import { todayLocalYmd } from '../utils/localDate'
import { readableForeground } from '../utils'
import { KanbanAvatarStack } from './KanbanAvatar'

export interface KanbanCardPeekMetaProps {
  card: KanbanCardModel
  resolveMember?: (userId: number) => KanbanMember | undefined
}

/** Canonical 4-level priority scale (mirrors KanbanContextMenu / the drawer's
 *  PRIORITY_OPTIONS). `1` = Normal is the default and stays silent so the row
 *  only flags the priorities that actually want attention. */
const PRIORITY_PILL: Record<number, { label: string; dot: string; text: string }> = {
  3: { label: 'Urgent', dot: 'bg-red-500', text: 'text-red-700 dark:text-red-300' },
  2: { label: 'High', dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-300' },
  0: { label: 'Low', dot: 'bg-neutral-400', text: 'text-neutral-500 dark:text-zinc-400' },
}

function formatDue(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Pure identity/meta strip. Always renders (so "whose card" is answered even
 *  when unassigned — the operator's first question), but stays a single quiet
 *  wrap so it never reintroduces the empty band it replaced. */
export function KanbanCardPeekMeta({ card, resolveMember }: KanbanCardPeekMetaProps) {
  const members = (card.assigned_user_ids ?? [])
    .map((id) => resolveMember?.(id))
    .filter((m): m is KanbanMember => m != null)

  const prio = PRIORITY_PILL[card.priority]
  const due = card.due_date ? card.due_date.slice(0, 10) : null
  const overdue =
    due != null &&
    card.status !== 'DONE' &&
    card.status !== 'ARCHIVED' &&
    due < todayLocalYmd()
  const tags = (card.tags ?? []).filter((t) => !t.revoked_at)

  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-neutral-100 px-3 py-2 dark:border-zinc-800">
      {members.length > 0 ? (
        <span className="inline-flex items-center" title={members.map((m) => m.name).join(', ')}>
          <KanbanAvatarStack members={members} max={4} size={20} />
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10.5px] font-medium text-neutral-500 dark:bg-zinc-800 dark:text-zinc-400">
          <UserRound className="h-3 w-3" aria-hidden />
          Unassigned
        </span>
      )}

      {prio && (
        <span
          className={`inline-flex items-center gap-1 rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10.5px] font-semibold dark:bg-zinc-800 ${prio.text}`}
          title={`Priority: ${prio.label}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${prio.dot}`} aria-hidden />
          {prio.label}
        </span>
      )}

      {due && (
        <span
          className={[
            'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium',
            overdue
              ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : 'bg-neutral-100 text-neutral-600 dark:bg-zinc-800 dark:text-zinc-300',
          ].join(' ')}
          title={overdue ? `Overdue — due ${formatDue(due)}` : `Due ${formatDue(due)}`}
        >
          {overdue ? <Flag className="h-3 w-3" aria-hidden /> : <CalendarClock className="h-3 w-3" aria-hidden />}
          {formatDue(due)}
        </span>
      )}

      {tags.slice(0, 4).map((t) => {
        const color = t.tag.color || '#94a3b8'
        return (
          <span
            key={t.id}
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none"
            style={{ background: color, color: readableForeground(color) }}
            title={t.tag.path}
          >
            {t.tag.name}
          </span>
        )
      })}
      {tags.length > 4 && (
        <span className="self-center text-[10px] font-medium text-neutral-400">+{tags.length - 4}</span>
      )}
    </div>
  )
}
